/**
 * ExploreCategoryRecipeAll - Browse all recipes section with random picks
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Container } from 'reactstrap';
import { Link } from 'react-router-dom';
import { ExploreCategory } from '../../Model/Explore/ExploreCategory';
import { Recipe, RecipeApi } from '../../Model/Diet/Recipe/Recipe';
import { useRootStore } from '../../Store/useRootStore';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useLocalized } from '../../Hooks/useLocalized';
import { Theme } from '../../Theme/Theme';
import { TileCard } from './TileCard';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Section = styled.div`
  padding: 32px 0 16px 0;
  background: #fafafa;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 16px;
`;

const TitleGroup = styled.div``;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: ${Theme.Colors.textColor};

  @media (max-width: 600px) {
    font-size: 1.25rem;
  }
`;

const Subtitle = styled.p`
  font-size: 0.9rem;
  color: ${Theme.Colors.charcoal};
  margin: 4px 0 0 0;
`;

const ShowAllLink = styled(Link)`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${Theme.Colors.primary};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const ScrollContainer = styled.div`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -16px;
    bottom: 0;
    width: 12px;
    background: linear-gradient(to right, rgba(250, 250, 250, 0.6) 0%, transparent 100%);
    pointer-events: none;
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: -16px;
    bottom: 0;
    width: 30px;
    background: linear-gradient(to left, rgba(250, 250, 250, 1) 0%, transparent 50%);
    pointer-events: none;
    z-index: 1;
  }
`;

const HorizontalScroll = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  overflow-y: visible;
  padding: 16px;
  padding-bottom: 32px;
  margin: -16px;
  margin-bottom: -32px;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CardWrapper = styled.div`
  flex: 0 0 280px;
  min-width: 280px;
  padding-bottom: 8px;

  @media (max-width: 600px) {
    flex: 0 0 220px;
    min-width: 220px;
  }
`;

const SkeletonCard = styled.div`
  flex: 0 0 280px;
  min-width: 280px;

  @media (max-width: 600px) {
    flex: 0 0 220px;
    min-width: 220px;
  }
`;

const SkeletonImage = styled.div`
  aspect-ratio: 1;
  border-radius: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const SkeletonText = styled.div<{ width?: string }>`
  height: 14px;
  width: ${({ width }) => width || '100%'};
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  margin-top: 12px;
`;

export type ExploreCategoryRecipeAllProps = {
  category: ExploreCategory;
  linkPrefix?: string;
};

export const ExploreCategoryRecipeAll: React.FC<ExploreCategoryRecipeAllProps> = observer(
  ({ category, linkPrefix = '/nutrition/' }) => {
    const { language } = useRootStore();
    const { t } = useTrans();

    const [recipes, setRecipes] = useState<Recipe[]>([]);
    const [loading, setLoading] = useState(true);

    const categoryName = useLocalized(category.name);
    const categoryDescription = useLocalized(category.description);

    const title = categoryName || t('nutrition.discover.title', { defaultValue: 'Discover Recipes' });
    const description = categoryDescription || t('nutrition.discover.subtitle', { defaultValue: 'Random picks to inspire your next meal' });

    useEffect(() => {
      setLoading(true);
      RecipeApi.random({ sampleSize: 8 })
        .then((result) => setRecipes(result))
        .finally(() => setLoading(false));
    }, []);

    const renderSkeletonCards = () =>
      Array.from({ length: 4 }).map((_, i) => (
        <SkeletonCard key={`skeleton-${i}`}>
          <SkeletonImage />
          <SkeletonText width="80%" />
          <SkeletonText width="50%" />
        </SkeletonCard>
      ));

    return (
      <Section>
        <Container>
          <SectionHeader>
            <TitleGroup>
              <Title>{title}</Title>
              <Subtitle>{description}</Subtitle>
            </TitleGroup>
            <ShowAllLink to={`${linkPrefix}library`}>{t('globals.showAll', { defaultValue: 'Show All' })}</ShowAllLink>
          </SectionHeader>
          <ScrollContainer>
            <HorizontalScroll>
              {loading
                ? renderSkeletonCards()
                : recipes.map((recipe) => (
                    <CardWrapper key={recipe.id}>
                      <TileCard entry={recipe} linkPrefix={linkPrefix} />
                    </CardWrapper>
                  ))}
            </HorizontalScroll>
          </ScrollContainer>
        </Container>
      </Section>
    );
  },
);
